import React from 'react';

interface AgtronData {
  wholeBean?: number;
  groundBean?: number;
}

interface RoastTypeCellProps {
  roastType: string | null | undefined;
  roastTypeLabel: string;
  agtron?: AgtronData;
}

/**
 * Displays the roast type badge with optional Agtron info centered beneath.
 *
 * Layout:
 *   [ Espresso Roast ]     ← badge, left-aligned in cell
 *     12/21 (-9)           ← agtron, centered relative to the badge above
 *
 * Formula: whole/ground (diff)
 *   diff = (whole - ground), 2 decimals, with leading "-" when negative
 */
export const RoastTypeCell: React.FC<RoastTypeCellProps> = ({
  roastType,
  roastTypeLabel,
  agtron,
}) => {
  const hasAgtron =
    agtron &&
    typeof agtron.wholeBean === 'number' &&
    typeof agtron.groundBean === 'number' &&
    (agtron.wholeBean > 0 || agtron.groundBean > 0);

  const agtronDiff =
    hasAgtron
      ? Number(((agtron!.wholeBean ?? 0) - (agtron!.groundBean ?? 0)).toFixed(2))
      : null;

  return (
    /**
     * `inline-flex flex-col items-center`:
     *   - `inline-flex` → stays left-aligned in the td (shrinks to content width)
     *   - `items-center` → agtron row is centered relative to the badge width
     */
    <div className="inline-flex flex-col items-center gap-1">
      {/* Roast type badge */}
      <span
        className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/20 uppercase tracking-wide whitespace-nowrap"
        data-test-id="roast-type-badge"
      >
        {roastType ? roastTypeLabel : '-'}
      </span>

      {/* Agtron sub-line — centered beneath the badge */}
      {hasAgtron && (
        <span
          className="text-[11px] text-primary-700 font-semibold tabular-nums"
          data-test-id="roast-type-agtron"
          title={`Whole: ${agtron!.wholeBean} / Ground: ${agtron!.groundBean}`}
        >
          {agtron!.wholeBean}/{agtron!.groundBean}{' '}
          <span className="text-primary-600">({agtronDiff})</span>
        </span>
      )}
    </div>
  );
};
